import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import classNames from 'clsx'
import OfferListLoadMoreButton from './OfferListLoadMoreButton'
import styled from 'styled-components'
import { rem } from 'polished'
import { EmptyArray, fillArray, sortBy, take, unique } from 'lib/array/arrayUtils'
import OfferListOfferTile from './OfferListTiles/OfferListOfferTile'
import noop from 'lib/function/noop'
import useMountedEffect from 'hooks/useMountedEffect'
import OfferListLoadingOfferTile from './OfferListTiles/OfferListLoadingOfferTile'
import OfferListEventsContext, { OfferListEvents } from './OfferListEventsContext'

import { EmptyObject } from 'lib/object/objectUtils'
import { OFFER_TYPE_TOUR, OFFER_TYPE_TOUR_V2 } from 'constants/offer'
import arraysEqual from 'lib/array/arraysEqual'
import { getSearchTypeFromFilters } from 'lib/search/searchUtils'
import useOfferList, { TrackingOptions } from 'hooks/Offers/useOfferList'
import { mediaQueryUp } from '../utils/breakpoint'
import useStreamEnded from 'hooks/Offers/useStreamEnded'

const OfferGrid = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  &.fullWidth {
    > * {
      &:first-child {
        padding: 0 ${rem(20)};
      }
    }
  }

  // Apply a space of 16px between all components
  > * + * {
    margin-top: ${rem(16)};
  }

  // Apply a space of 0 between all offer tiles components
  > .offer-tile + .offer-tile {
    margin-top: 0;
  }

  // For above tablet width re-apply a space of 0 between all offer tiles components
  ${mediaQueryUp.tablet} {
    > .offer-tile + .offer-tile {
      margin-top: ${rem(16)};
    }
  }
`

function tryGetOfferListLoaderType(filters: App.OfferListFilters): App.OfferListOfferType | undefined {
  const { offerTypes } = filters

  if (!offerTypes) {
    return
  }

  if (arraysEqual(offerTypes, [OFFER_TYPE_TOUR, OFFER_TYPE_TOUR_V2])) {
    return OFFER_TYPE_TOUR_V2
  }

  if (offerTypes.length === 1) {
    return offerTypes[0]
  }
}

export interface OfferListV2Item {
  id: string
  position: number | 'start' | 'end'
  show?: boolean | 'hasOffers' | 'hasNoOffers' | 'always'
  columnWidth?: number
  element?: React.ReactNode
}

interface Props {
  className?: string
  filters?: App.OfferListFilters
  interstitials?: Array<OfferListV2Item>
  random?: boolean
  /**
   * @default 4
   */
  pageSize?: number
  eagerLoadFirstImage?: boolean
  /**
   * @default frontpage
   */
  tileStyle?: 'frontpage' | 'search' | 'hero'
  /**
   * @default 1
   */
  defaultPage?: number
  onPageChange?: (nextPage: number) => void
  /**
   * @default infinite
   */
  maxOffers?: number | 'infinite'
  /**
   * Adds the ability to provide your own offer list
   **/
  extraOfferListAndAction?: OfferListAndAction
  /**
   * Whether or not to filter out the sold out offers returned in the list
   * Note: not all end points return sold out, but for those that do, this will filter them
   * @defaults false
   */
  filterSoldOut?: boolean
  /**
   * Offer ids to exclude from the offer list
   **/
  excludedIds?: Array<string>

  /**
   * Whether or not the offer list is rendered in full width
   */
  fullWidth?: boolean

  /**
   * Whether or not the client will check the availability of the offer
   */
  clientCheckAvailability?: boolean

  /**
   * Whether or not the offer list is rendered in infinite scroll
   */
  isInfiniteScroll?: boolean
  searchId?: string
  typeaheadId?: string
  showLoadingTiles?: boolean
  trackingOptions?: TrackingOptions
  /**
   * Callback function that is called when there are no more offers to load
   */
  onEndReached?: () => void
  overrideFetching?: boolean
  disableUseOfferList?: boolean
  showLoadMoreTiles?: boolean
  minRequiredOffers?: number
  /**
   * Whether to prevent navigation when clicking on offer tiles
   * @default false
   */
  preventNavigationToOfferPage?: boolean
  storeOfferListTile?: boolean
}

export enum OfferListAction {
  OVERRIDE = 'override',
  PRIORITIZE = 'prioritize', // prioritize the new list offers
}

export interface OfferListAndAction {
  action: OfferListAction
  offerList: App.OfferList
}

/**
 * Features to add support for:
 * - excluded offer ids
 * - minimum offers before displaying
 */
function OfferList(props: Props) {
  const {
    className,
    filters = EmptyObject,
    interstitials = EmptyArray,
    random,
    pageSize = 4,
    eagerLoadFirstImage,
    tileStyle = 'frontpage',
    defaultPage = 1,
    onPageChange = noop,
    maxOffers = 'infinite',
    extraOfferListAndAction,
    filterSoldOut,
    excludedIds,
    clientCheckAvailability,
    isInfiniteScroll = true,
    searchId,
    typeaheadId,
    showLoadingTiles = true,
    trackingOptions,
    onEndReached = noop,
    overrideFetching,
    disableUseOfferList,
    showLoadMoreTiles = true,
    minRequiredOffers,
    preventNavigationToOfferPage = false,
    storeOfferListTile = false,
  }: Props = props

  const streamResults = filters?.isStream
  const [page, setPage] = useState<number>(defaultPage)
  const dispatchOfferListEvent = useContext(OfferListEventsContext)

  const incrementPage = useCallback(() => {
    setPage((prevState) => prevState + 1)
  }, [])

  useMountedEffect(() => {
    onPageChange(page)
  }, [page])

  const streamEnded = useStreamEnded(filters, { disabled: !streamResults })

  const searchType = getSearchTypeFromFilters(filters)

  const localOfferlist = useOfferList(filters, {
    randomize: random,
    disabled: (disableUseOfferList ?? extraOfferListAndAction?.action === OfferListAction.OVERRIDE),
    // TODO: this component shouldn't know about search type, move it to the implementation instead
    filterSoldOut: !streamResults && (filterSoldOut || searchType === 'destination'),
    excludedIds,
    clientCheckAvailability,
    minRequiredOffers,
    searchId,
    typeaheadId,
  }, trackingOptions)

  useEffect(() => {
    // When the key of the list changes (we have a new list) reset the page to the default page
    setPage(defaultPage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localOfferlist.key])

  // construct a new offerList based on the new offerList action
  const offerList = useMemo(() => {
    let updatedOfferList: App.OfferList = localOfferlist

    if (extraOfferListAndAction?.action === OfferListAction.OVERRIDE) {
      updatedOfferList = extraOfferListAndAction.offerList
    } else if (extraOfferListAndAction?.action === OfferListAction.PRIORITIZE) {
      const localOfferlistOfferIds = localOfferlist.offerIds
      const extraOfferlistOfferIds = extraOfferListAndAction.offerList.offerIds
      // sort the localOfferlist offerIds based on the order of newOfferlist offerIds
      const localOfferlistOfferIdsSet = new Set(localOfferlistOfferIds)
      const commonIds = extraOfferlistOfferIds.filter(item => localOfferlistOfferIdsSet.has(item))
      const finalOfferIds = unique([...commonIds, ...localOfferlistOfferIds])

      updatedOfferList = {
        fetching: extraOfferListAndAction.offerList.fetching || localOfferlist.fetching,
        error: extraOfferListAndAction.offerList.error || localOfferlist.error,
        offerIds: finalOfferIds,
        key: localOfferlist.key,
        offerCount: finalOfferIds.length,
        searchVertical: localOfferlist.searchVertical,
      }
    }

    return updatedOfferList
  }, [extraOfferListAndAction, localOfferlist])

  // quick dirty optimisation to not pass filters to front page and break memoisation
  // only search style tiles *require* the filters object
  const searchFilters = tileStyle === 'search' ? filters : undefined
  const offerListFetching = offerList.fetching || overrideFetching
  const shownElementCount = page * pageSize

  const totalOfferCount = Math.min(
    (maxOffers === 'infinite' ? offerList.offerIds.length : maxOffers),
    offerList.offerIds.length,
  )

  useEffect(() => {
    if (!offerListFetching) {
      dispatchOfferListEvent({
        type: OfferListEvents.listLoad,
        key: offerList.key,
        list: offerList,
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerListFetching, offerList.key])

  let offerItems: Array<OfferListV2Item>

  if (offerListFetching) {
    // list is fetching, return 4 placeholder tiles of the current style
    offerItems = showLoadingTiles ? fillArray(4).map<OfferListV2Item>((index) => ({
      id: `${index}-loader`,
      position: 'start',
      element: <OfferListLoadingOfferTile
        tileStyle={tileStyle}
        offerType={tryGetOfferListLoaderType(filters)}
        className="offer-tile"
        filters={searchFilters}
        offerPosition={index}
      />,
    })) : []
  } else {
    const offerIds = take(offerList.offerIds, maxOffers === 'infinite' ? shownElementCount : maxOffers)
    // maximum number of offers we'll show is the max count, so only process them
    offerItems = offerIds.map<OfferListV2Item>((id, index) => ({
      id,
      position: index,
      element: <OfferListOfferTile
        offerId={id}
        position={index}
        eagerLoadImage={index === 0 && eagerLoadFirstImage}
        filters={searchFilters}
        tileStyle={tileStyle}
        wrapperClass="offer-tile"
        preventNavigationToOfferPage={preventNavigationToOfferPage}
        storeOfferListTile={storeOfferListTile}
      />,
    }))
  }

  // Extras are added first as if they have a position set, we want to that to go first
  const filteredExtras = interstitials.filter(item => {
    // we don't know what the 'end' or a position is until we actually have the list
    if (item.position !== 'start' && offerListFetching) return false

    if (item.show === 'always') return true
    if (item.show === 'hasOffers') return !offerListFetching && offerItems.length > 0
    if (item.show === 'hasNoOffers') return !offerListFetching && offerItems.length === 0
    // wondering why your item is showing when it shouldn't? Always cast to a boolean
    if (typeof item.show === 'boolean') return item.show

    return true
  })

  const combinedItems = filteredExtras.concat(offerItems)
  const visibleItems = sortBy(combinedItems, item => {
    if (item.position === 'end') {
      return 10000
    } else if (item.position === 'start') {
      return -1
    } else {
      return item.position
    }
  }, 'asc')

  const totalItemCount = totalOfferCount + filteredExtras.length
  const hasMore = streamResults ?
      (!streamEnded || (totalItemCount > shownElementCount)) :
    totalItemCount > shownElementCount

  useEffect(() => {
    if (!hasMore) {
      onEndReached()
    }
  }, [hasMore, onEndReached])

  return (
    <OfferGrid
      className={classNames(className, { fullWidth: props.fullWidth })}
      data-testid="offer-list"
    >
      {take(visibleItems, shownElementCount).map(item => <React.Fragment key={item.id}>
        {item.element}
      </React.Fragment>,
      )}
      {hasMore && showLoadingTiles && showLoadMoreTiles &&
        <OfferListLoadMoreButton
          onClick={incrementPage}
          size="large"
          onScrollNear={isInfiniteScroll ? incrementPage : undefined}
          showLoadingIndicator={streamResults && !streamEnded}
        >
          Load more
        </OfferListLoadMoreButton>}
    </OfferGrid>
  )
}

export default OfferList
